import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { DataResult, process } from '@progress/kendo-data-query';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { InputChangeEvent, Switch } from '@progress/kendo-react-inputs';
import { Card, CardBody, CardTitle, PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import React, { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { CommonResources, Resources } from '../../config/CommonResources';
import { EntityStatusEnum, EntityStatusType, ProductTypeEnum } from '../../config/Enums';
import { changeReportsTagSelection, TOGGLE_SHOW_ACCESSIBLE_REPORTS } from '../../store/actions/navActions';
import { getSecurityProfile } from '../../store/actions/profileActions';
import { RootState } from '../../store/reducer/rootReducer';
import { OnDemandIcon } from '../helpers/IconLoader';
import useAppModel, { QueryTypeEnum } from '../helpers/ModelHook';
import useAppRoutes from '../helpers/RouteHook';
import { Nullable } from '../helpers/Utils';
import { GET_DATACATALOG_SUMMARY, RenderDashboard } from '../pages/catalog/ReportCatalogSummary';
import { GET_MAM_ACCESS_REQUEST_STATUSES, SYNC_MAM_ACCESS_REQUESTS } from '../services/GraphQLMatrixAccessManager';
import { IMenuItemProps, IUserInfoProps } from '../services/GraphQLShared';
import { embedTagsConfig } from '../services/GraphQLTypes';
import JumboBox from '../shared/JumboBox';
import { SearchBox } from './boxes/StatusBox';
import { GenericLoadingPanel } from './panels/Panels';
import WelcomeNoAuthorized from './WelcomeNoAuthorized';

export const WelcomeHeader = (user: Nullable<IUserInfoProps> = null): React.ReactElement => {
  return (
    <>
      <span className="name">
        {CommonResources.Hello} <span>{user?.givenName ?? 'Stranger'}!</span>
      </span>
      {/* <span className="welcome-message">Welcome to {Resources.ApplicationName}</span> */}
      <span className="welcome-message">Welcome,</span>
      <p
        dangerouslySetInnerHTML={{
          __html: user ? Resources.WelcomeMesaage : `${Resources.ApplicationIntro}${Resources.WelcomeMesaage}`,
        }}
      ></p>
    </>
  );
};

export const getGroupedProducts = (products: any[]): DataResult => {
  return process(products, {
    group: [
      {
        field: 'section.order',
        aggregates: [
          { aggregate: 'min', field: 'section.display' },
          { aggregate: 'min', field: 'section.id' },
        ],
      },
    ],
    sort: [
      { field: 'access', dir: 'desc' },
      { field: 'reportName', dir: 'asc' },
    ],
  });
};

export const getInitialExpandedIds = (
  dataProducts: any,
  queryNode: string,
  currentSecurity: any,
  expandMode: 'all' | 'accessible' | string[] = 'all',
  showAllReports: boolean,
  accessibleProducts: any[] = [],
): string[] => {
  if (!dataProducts?.[queryNode]) return [];

  const profiles = currentSecurity.profile?.operationProfiles;

  const sourceProducts = showAllReports ? dataProducts[queryNode] : accessibleProducts;

  const productsCopy = sourceProducts
    .filter((e: any) => e.section?.status === EntityStatusType.Active)
    .map((e: any) => ({
      ...e,
      access: e.groups && profiles?.some((r: any) => e.groups.toLowerCase().indexOf(r.entityId.toLowerCase()) > -1),
    }));

  const groupProducts = getGroupedProducts(productsCopy);

  return groupProducts.data
    .map((group: any, i: number) => {
      const sectionName: string = group.aggregates['section.display'].min;
      let shouldExpand = false;

      if (expandMode === 'all') {
        shouldExpand = true;
      } else if (expandMode === 'accessible') {
        shouldExpand = group.items.some((item: any) => item.access);
      } else {
        shouldExpand = (expandMode as string[]).some((pattern) => sectionName.trim().toLowerCase().includes(pattern.trim().toLowerCase()));
      }

      return shouldExpand ? `.${i}` : null;
    })
    .filter(Boolean) as string[];
};

export const LoadCatalog = (
  dataProducts,
  state,
  currentSecurity,
  navigate,
  accessibleProducts,
  initialExpandedIds: string[] = [],
  accessRequestStatusByProductId: Record<string, string> = {},
): React.ReactElement[] => {
  const allProducts = dataProducts[GET_DATACATALOG_SUMMARY.queryNode];
  let selectedProducts = state.showAllReports ? allProducts : accessibleProducts;
  if (state.selectedTags.length > 0)
    selectedProducts = selectedProducts?.filter((e: any) => e.formatTags.some((r: any) => state.selectedTags.indexOf(r.id) >= 0));
  if (state.search) {
    const searchLower = state.search.toLowerCase();
    selectedProducts = selectedProducts.filter(
      (e: any) =>
        ~e.reportName.toLowerCase().indexOf(searchLower) ||
        ~e.section.display.toLowerCase().indexOf(searchLower) ||
        ~e.market.display.toLowerCase().indexOf(searchLower) ||
        e.formatTags.some((r: any) => ~r.display.toLowerCase().indexOf(searchLower)),
    );
  }
  const profiles = currentSecurity.profile.operationProfiles;
  const selectedProductsCopy: any[] =
    selectedProducts
      ?.filter((e) => e.section?.status === EntityStatusType.Active)
      .map((e) => {
        const hasAccess = e.groups && profiles.some((r: any) => e.groups.toLowerCase().indexOf(r.entityId.toLowerCase()) > -1);
        // The cached request status is only relevant for products the user cannot yet access.
        const requestStatus = !hasAccess ? accessRequestStatusByProductId[e.id] ?? null : null;
        return {
          ...e,
          access: hasAccess,
          requestStatus,
        };
      }) || [];

  if (!state.showAllReports) {
    if (accessibleProducts?.length <= 0) {
      return [<div className="d-flex justify-content-center panel-error-msg">{CommonResources.Message_NoAccessibleProduct}</div>];
    }
  }

  const groupProducts = getGroupedProducts(selectedProductsCopy);
  const classes = {};
  groupProducts.data.forEach((group) => {
    const imageUrl = `/images/icons/${group.aggregates['section.id'].min}`;
    const uniqueClass = `bg-${btoa(imageUrl).replace(/[^a-zA-Z0-9]/g, '')}`;
    if (!document.getElementById(uniqueClass)) {
      const style = document.createElement('style');
      style.id = uniqueClass;
      style.innerHTML = `.${uniqueClass} { background-image: url('${imageUrl}'); background-size: cover; background-position: center; }`;
      document.head.appendChild(style);
    }
    classes[group.aggregates['section.id'].min] = uniqueClass;
  });
  return groupProducts.data.map((group: any, i: number) => (
    <PanelBarItem
      key={i}
      title={group.aggregates['section.display'].min}
      expanded={initialExpandedIds.includes(`.${i}`)}
      className={`panel-bar-item-container fw-bold pb-2 ${classes[group.aggregates['section.id'].min]}`}
    >
      <div className="my-2 k-card-group" style={{ flexFlow: 'row wrap' }}>
        {group.items.map((e: any, j: any) => (
          <RenderDashboard
            key={j}
            item={e}
            navigateTo={() =>
              navigate(
                e.type == ProductTypeEnum.Link || e.type == ProductTypeEnum.PowerBiApp
                  ? `/embedded/${e.id}`
                  : e.type == ProductTypeEnum.CrossTab
                  ? `/crosstab/${e.id}`
                  : `/reportviewer/${e.id}`,
              )
            }
          />
        ))}
      </div>
    </PanelBarItem>
  ));
};

const INITIAL_EXPAND_MODE: 'all' | 'accessible' | string[] = ['atlas'];
const selectSecurity = (state: RootState) => state.security;
const showAllReportsNav = (state: RootState) => state.nav.showAllReports;

function Dashboard(): React.ReactElement {
  const navigate = useNavigate();
  const { getDashboardPanels } = useAppRoutes();
  const { buildQuery } = useAppModel();
  const GET_EMBED_TAG_LIST = buildQuery(embedTagsConfig, QueryTypeEnum.GetList);

  const currentSecurity = useSelector(selectSecurity);
  const [panels] = React.useState<IMenuItemProps[]>(getDashboardPanels());
  const [expandedReady, setExpandedReady] = React.useState(false);
  const initialExpandedIdsRef = React.useRef<string[]>([]);
  const handleClick = (box: IMenuItemProps) => navigate(box.route);

  const { loading: loadingTags, data: tags } = useQuery(GET_EMBED_TAG_LIST.query, {
    variables: { entityStatus: EntityStatusEnum.Active },
  });
  // Refresh the current user's pending MAM request statuses before reading them. Once the
  // sync has settled, the statuses and the products load in parallel - neither depends on
  // the other, only on sync having finished - and are merged once both are back.
  const [syncAccessRequests] = useMutation(SYNC_MAM_ACCESS_REQUESTS);
  const [accessRequestsSynced, setAccessRequestsSynced] = React.useState(false);
  const apolloClient = useApolloClient();
  const dispatch = useDispatch();

  useEffect(() => {
    // When the sync detects status changes (e.g. a request got approved), the backend has just
    // evicted this user's cached security profile - refetch it now so the new permissions apply
    // on this load instead of requiring another refresh.
    syncAccessRequests()
      .then((result) => {
        const updatedCount = result?.data?.mam?.syncAccessRequests ?? 0;
        if (updatedCount > 0) return dispatch(getSecurityProfile(apolloClient) as any);
      })
      .finally(() => setAccessRequestsSynced(true));
  }, []);

  const { loading: accessRequestStatusesLoading, data: accessRequestStatusesData } = useQuery(GET_MAM_ACCESS_REQUEST_STATUSES.query, {
    skip: !accessRequestsSynced,
  });
  const accessRequestStatusByProductId: Record<string, string> = (
    accessRequestStatusesData?.mam?.accessRequestStatuses ?? []
  ).reduce((map: Record<string, string>, entry: any) => {
    map[entry.ProductId] = entry.Status;
    return map;
  }, {});

  const {
    loading,
    error,
    data: dataProducts,
  } = useQuery(GET_DATACATALOG_SUMMARY.query, {
    variables: { entityStatus: EntityStatusEnum.Active },
    skip: !accessRequestsSynced,
  });
  const data = loadingTags ? [] : tags[GET_EMBED_TAG_LIST.queryNode];
  const selectedReportTags = useSelector((state: RootState) => state.nav.selectedReportTags);
  const { tag } = useParams();
  const selectedTags = data.filter((item) => selectedReportTags?.includes(item.id));
  const showAllReports = useSelector(showAllReportsNav);
  const [accessibleProducts, setAccessibleProducts] = React.useState<any[]>([]);
  const [state, setState] = React.useState<any>({
    selectedTags: [] as any,
    atlasTag: '',
    search: '',
    showAllReports: showAllReports ?? true,
  });

  useEffect(() => {
    if (dataProducts && dataProducts[GET_DATACATALOG_SUMMARY.queryNode]) {
      const profiles = currentSecurity.profile?.operationProfiles;
      const productsCopy = dataProducts[GET_DATACATALOG_SUMMARY.queryNode].map((e: any) => {
        const hasAccess = e.groups && profiles?.some((r: any) => e.groups.toLowerCase().indexOf(r.entityId.toLowerCase()) > -1);
        return { ...e, access: hasAccess };
      });
      const accessibleProductsList = productsCopy.filter((e: any) => e.access);
      setAccessibleProducts(accessibleProductsList);
      const effectiveShowAll = (accessibleProductsList?.length ?? 0) === 0 ? true : showAllReports ?? true;
      setState((prevState) => ({
        ...prevState,
        selectedTags: (selectedReportTags?.length ?? 0) > 0 ? selectedReportTags : [],
        showAllReports: effectiveShowAll,
      }));
      setExpandedReady(false);
      const ids = getInitialExpandedIds(
        dataProducts,
        GET_DATACATALOG_SUMMARY.queryNode,
        currentSecurity,
        INITIAL_EXPAND_MODE,
        effectiveShowAll,
        accessibleProductsList,
      );
      initialExpandedIdsRef.current = ids;
      setExpandedReady(true);
    }
  }, [selectedReportTags, dataProducts, showAllReports]);

  useEffect(() => {
    if (tag && data.length > 0) {
      const tagMatches = data
        .filter((item) => tag.toUpperCase() === item.name.toUpperCase())
        .map((item) => item.id)
        .filter((id) => !selectedReportTags.includes(id));

      if (tagMatches.length > 0) {
        dispatch(changeReportsTagSelection([...selectedReportTags, ...tagMatches]));
      }
    }
  }, [tag, data]);

  const handleMultiselectTagChange = (event) => {
    const ids = event.value.map((item) => item.id);
    dispatch(changeReportsTagSelection(ids));
    setState({
      ...state,
      selectedTags: ids,
    });
    if (tag) {
      const tagId = data.find((item) => tag.toUpperCase() == item.name.toUpperCase());
      if (tagId && !ids.includes(tagId.id)) {
        navigate('/');
      }
    }
  };

  const handleSwitchChange = (event) => {
    dispatch({ type: TOGGLE_SHOW_ACCESSIBLE_REPORTS });
    setState({
      ...state,
      showAllReports: event.value,
    });
  };

  const renderPanels = (title: string, panels: IMenuItemProps[], styleName: string) => {
    return (
      <>
        <Row>
          <Col style={{ textAlign: 'center' }} className="mt-3 mb-2">
            <h4 className="mb-1 white-text">{title}</h4>
          </Col>
        </Row>
        <Row>
          {panels.map((box: IMenuItemProps, i: number) => (
            <Col key={i} className="p-2 with-pointer" onClick={() => handleClick(box)}>
              <Card className={`shadow-none text-center ${styleName}`}>
                <CardBody>
                  <OnDemandIcon className="white-text mb-3" icon={box.icon} size="3x" />
                  <CardTitle className="fr-white white-text">{box.title}</CardTitle>
                  <p className="d-none d-lg-block white-text admin-card-desc">{box.description}</p>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  };
  const handleSearchChange = (e: InputChangeEvent) => setState({ ...state, search: e.value });
  return currentSecurity.isAuthorized ? (
    <div className="app__content">
      <div>
        <Container fluid className="welcome-jumbo mt-4 px-5">
          <Row className="mb-3">
            <Col className="welcome-header">{WelcomeHeader(currentSecurity.userInfo)}</Col>
            <Col md={6} className="dashboard-filter-section">
              <SearchBox search={state.search} handleSearchChange={handleSearchChange} className="mt-3 mb-3 pe-4" />
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <MultiSelect
                  data={data}
                  onChange={handleMultiselectTagChange}
                  value={selectedTags}
                  placeholder="All types"
                  textField="name"
                  dataItemKey="id"
                  className="tags-dropdown"
                />
                <div>
                  <Switch checked={state.showAllReports} onChange={handleSwitchChange} className="ms-3 me-1" />
                  <label className="switch-label">Show All</label>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="my-2">
            <Col>
              {loading || accessRequestStatusesLoading || !expandedReady ? (
                <GenericLoadingPanel loading={true} />
              ) : error ? (
                <div className="d-flex justify-content-center panel-error-msg">{CommonResources.Message_ErrorLoadingProducts}</div>
              ) : (
                <PanelBar animation={false} expandMode="multiple" className={'product-catalog'}>
                  {LoadCatalog(
                    dataProducts,
                    state,
                    currentSecurity,
                    navigate,
                    accessibleProducts,
                    initialExpandedIdsRef.current,
                    accessRequestStatusByProductId,
                  )}
                </PanelBar>
              )}
            </Col>
          </Row>
        </Container>
        {panels.length > 0 && currentSecurity.profile?.admin && (
          <JumboBox styleName="admin-section mb-0">{renderPanels(Resources.Settings, panels, 'admin-section')}</JumboBox>
        )}
      </div>
      {/* <Footer /> */}
    </div>
  ) : (
    <WelcomeNoAuthorized user={currentSecurity.userInfo} />
  );
}

export default React.memo(Dashboard);
